import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { encodeAuthPassword, shouldRetryRawPassword } from "@/lib/passwordCodec";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: Record<string, string>) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // IMPORTANT: getSession must resolve BEFORE we trust onAuthStateChange
    // to avoid a race condition where onAuthStateChange fires with null session
    // before the persisted session is restored from storage.
    let initialised = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Only update state after initial session is restored
      if (!initialised) return;

      setSession(session);
      setUser(session?.user ?? null);

      // Send welcome email on first login after email verification
      if (event === 'SIGNED_IN' && session?.user?.email_confirmed_at) {
        const welcomeKey = `welcome_email_sent_${session.user.id}`;
        if (!localStorage.getItem(welcomeKey)) {
          localStorage.setItem(welcomeKey, 'true');
          supabase.functions.invoke('send-welcome-email').catch(() => {
            // best-effort, edge function has its own dedup
          });
        }
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      initialised = true;
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata?: Record<string, string>) => {
    const encodedPassword = await encodeAuthPassword(email, password);
    const { data, error } = await supabase.auth.signUp({
      email,
      password: encodedPassword,
      options: { data: metadata, emailRedirectTo: window.location.origin },
    });
    // Immediately set session so ProtectedRoute sees the user
    // before onAuthStateChange fires asynchronously
    if (!error && data.session) {
      setSession(data.session);
      setUser(data.session.user);
    }
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const encodedPassword = await encodeAuthPassword(email, password);
    const transformedAttempt = await supabase.auth.signInWithPassword({ email, password: encodedPassword });

    if (!transformedAttempt.error) {
      return { error: null };
    }

    if (!shouldRetryRawPassword(transformedAttempt.error.message)) {
      return { error: transformedAttempt.error as Error | null };
    }

    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
